// web/audio.ts — BROOD's sound layer (render-only).
//
// A thin WebAudio engine plus a SYNTHESIZED cue bank — no audio files, so SFX add
// zero download weight and need no loader. Like every other web layer it is strictly
// presentation: it never touches the sim, the action log, determinism, or the state
// hash, and it degrades to silence wherever WebAudio is missing.
//
// PURE vs PAINT split (the house discipline): the cue BANK and the tier→cue mappings
// are plain data/functions, unit-tested headlessly (web/audio.test.mjs). Only the
// `AudioEngine` actually touches `AudioContext`, and it does so lazily (gesture-gated)
// so importing the module needs no browser — Node tests import only the pure parts.

// --- Cue bank (pure data) ---------------------------------------------------

/** One oscillator voice: a wave at `freq` (optionally swept to `freqEnd`) with an
 *  attack/decay envelope, optionally delayed from the cue's start. */
export interface ToneLayer {
  wave: OscillatorType;
  freq: number;
  freqEnd?: number;
  gain: number;
  attack: number;
  decay: number;
  delay?: number;
}

/** One filtered-noise burst (for transients like the box "crack"). */
export interface NoiseLayer {
  gain: number;
  attack: number;
  decay: number;
  delay?: number;
  filterHz?: number;
}

/** A named sound = a stack of tone + noise layers played together. */
export interface Cue {
  tones?: ToneLayer[];
  noise?: NoiseLayer[];
}

export type CueId =
  | 'click' | 'boxCharge' | 'boxBurst' | 'reveal' | 'revealRare'
  | 'fanfareMinor' | 'fanfareApex' | 'fanfareMiracle'
  | 'geneApply' | 'ascend' | 'fusion' | 'objective'
  | 'discovery' | 'apexAlert' | 'region';

/** The synthesized sound bank. Gains are deliberately modest — layers + the master
 *  gain stack up, and these play over a quiet sim. Tuned by ear; all values finite. */
export const SOUND_BANK: Record<CueId, Cue> = {
  // UI
  click:       { tones: [{ wave: 'square', freq: 240, gain: 0.10, attack: 0.004, decay: 0.05 }] },
  geneApply:   { tones: [{ wave: 'sine', freq: 440, freqEnd: 660, gain: 0.13, attack: 0.005, decay: 0.12 }] },
  ascend:      { tones: [{ wave: 'sine', freq: 330, freqEnd: 990, gain: 0.15, attack: 0.02, decay: 0.34 }] },
  fusion:      { tones: [{ wave: 'sawtooth', freq: 220, freqEnd: 330, gain: 0.13, attack: 0.01, decay: 0.28 }] },
  objective:   { tones: [
    { wave: 'triangle', freq: 660, gain: 0.15, attack: 0.005, decay: 0.16 },
    { wave: 'triangle', freq: 990, gain: 0.13, attack: 0.005, decay: 0.20, delay: 0.10 },
  ] },

  // Evolution-box reveal
  boxCharge:   { tones: [{ wave: 'sine', freq: 110, freqEnd: 440, gain: 0.16, attack: 0.18, decay: 0.16 }] },
  boxBurst:    {
    tones: [{ wave: 'sine', freq: 90, freqEnd: 48, gain: 0.26, attack: 0.005, decay: 0.34 }],
    noise: [{ gain: 0.20, attack: 0.004, decay: 0.18, filterHz: 1800 }],
  },
  reveal:      { tones: [{ wave: 'triangle', freq: 523, gain: 0.15, attack: 0.005, decay: 0.14 }] },
  revealRare:  { tones: [
    { wave: 'sine', freq: 880, gain: 0.13, attack: 0.005, decay: 0.20 },
    { wave: 'sine', freq: 1318, gain: 0.07, attack: 0.005, decay: 0.22, delay: 0.04 },
  ] },
  fanfareMinor: { tones: [
    { wave: 'triangle', freq: 523, gain: 0.17, attack: 0.01, decay: 0.18 },
    { wave: 'triangle', freq: 659, gain: 0.17, attack: 0.01, decay: 0.22, delay: 0.12 },
  ] },
  fanfareApex: { tones: [
    { wave: 'sine', freq: 131, gain: 0.20, attack: 0.01, decay: 0.50 },
    { wave: 'sawtooth', freq: 392, gain: 0.13, attack: 0.01, decay: 0.20 },
    { wave: 'sawtooth', freq: 523, gain: 0.13, attack: 0.01, decay: 0.22, delay: 0.12 },
    { wave: 'sine', freq: 784, gain: 0.15, attack: 0.01, decay: 0.30, delay: 0.24 },
  ] },
  fanfareMiracle: { tones: [
    { wave: 'sine', freq: 131, gain: 0.20, attack: 0.02, decay: 0.70 },
    { wave: 'sine', freq: 523, gain: 0.13, attack: 0.01, decay: 0.20 },
    { wave: 'sine', freq: 659, gain: 0.13, attack: 0.01, decay: 0.20, delay: 0.10 },
    { wave: 'sine', freq: 784, gain: 0.13, attack: 0.01, decay: 0.20, delay: 0.20 },
    { wave: 'sine', freq: 1046, gain: 0.17, attack: 0.01, decay: 0.50, delay: 0.30 },
    { wave: 'triangle', freq: 1568, gain: 0.09, attack: 0.01, decay: 0.60, delay: 0.42 },
  ] },

  // Event stingers (notification/history feed)
  discovery:   { tones: [
    { wave: 'sine', freq: 1046, gain: 0.13, attack: 0.005, decay: 0.24 },
    { wave: 'sine', freq: 1568, gain: 0.08, attack: 0.005, decay: 0.26, delay: 0.05 },
  ] },
  apexAlert:   { tones: [
    { wave: 'sawtooth', freq: 110, gain: 0.20, attack: 0.02, decay: 0.40 },
    { wave: 'sawtooth', freq: 116, gain: 0.16, attack: 0.02, decay: 0.42 },
  ] },
  region:      { tones: [{ wave: 'sine', freq: 392, freqEnd: 784, gain: 0.16, attack: 0.02, decay: 0.30 }] },
};

/** All cue ids (for tests + iteration). */
export const CUE_IDS: CueId[] = Object.keys(SOUND_BANK) as CueId[];

/** When a layer finishes, relative to the cue's start (seconds). */
export function layerEnd(l: { attack: number; decay: number; delay?: number }): number {
  return (l.delay ?? 0) + l.attack + l.decay;
}

/** Total duration of a cue (the latest layer end), in seconds. */
export function cueEnd(cue: Cue): number {
  let e = 0;
  for (const t of cue.tones ?? []) e = Math.max(e, layerEnd(t));
  for (const n of cue.noise ?? []) e = Math.max(e, layerEnd(n));
  return e;
}

/**
 * The burst fanfare for a box tier (Mutation/Aberration/Apex/Miracle), or null for
 * the everyday Adaptation tier (which gets only the burst). Mirrors the visual
 * escalation in `boxReveal.tierEffect`.
 */
export function tierFanfare(tier: number): CueId | null {
  if (tier >= 4) return 'fanfareMiracle';
  if (tier >= 3) return 'fanfareApex';
  if (tier >= 1) return 'fanfareMinor';
  return null;
}

/** Clamp a volume into [0,1] (NaN → 0). */
export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// --- Ambient colony bed (pure mapping) --------------------------------------

/** Resolved parameters for the evolving ambient hum at a given intensity. */
export interface AmbientParams {
  /** Bed gain feeding the master (already scaled by intensity); 0 at intensity 0. */
  gain: number;
  /** Drone fundamental (Hz) — rises a little as the colony thrives. */
  baseHz: number;
  /** The second voice's frequency (a fixed musical ratio above the fundamental). */
  fifthHz: number;
  /** LFO rate (Hz) — the slow tremolo "breathes" faster when busier. */
  lfoHz: number;
  /** LFO tremolo depth as a fraction of `gain` (0..1). */
  lfoDepth: number;
}

/** Two voices a perfect fifth apart read as a calm, consonant drone. */
const AMBIENT_FIFTH = 1.5;
/** Ceiling gain for the whole bed — deliberately quiet so cues sit on top of it. */
const AMBIENT_MAX_GAIN = 0.06;

/**
 * Map a 0..1 colony-intensity (derived by the caller from live population/activity)
 * to the ambient bed's parameters. PURE + finite → unit-tested headlessly.
 *
 * Design: a thriving hive should *sound* busier without ever getting loud. Intensity
 * raises the gain (with a small floor so a living colony is faintly audible), nudges
 * the drone up a few semitones, and speeds the tremolo. Intensity 0 returns gain 0
 * (silent) so an empty/extinct colony makes no sound.
 */
export function ambientParams(intensity: number): AmbientParams {
  const t = clampVolume(intensity); // reuse the [0,1]/NaN guard
  // gain: 0 at t==0, else a small floor + a linear climb to the ceiling.
  const gain = t <= 0 ? 0 : (0.18 + 0.82 * t) * AMBIENT_MAX_GAIN;
  // baseHz: 55 Hz (A1) → ~73 Hz, a gentle rise (~a fourth) as the colony grows.
  const baseHz = 55 + 18 * t;
  return {
    gain,
    baseHz,
    fifthHz: baseHz * AMBIENT_FIFTH,
    // 0.05 Hz (one swell / 20s) → 0.22 Hz (one swell / ~4.5s) when bustling.
    lfoHz: 0.05 + 0.17 * t,
    // Deeper tremolo when busier, but never fully gated (0.25 → 0.6).
    lfoDepth: 0.25 + 0.35 * t,
  };
}

// --- Audio engine (the only part that touches WebAudio) ---------------------

const MAX_VOICES = 12; // concurrent cues; extra triggers drop silently

class AudioEngine {
  private ac: AudioContext | null = null;
  private master: GainNode | null = null;
  private ready = false;
  private muted = false;
  private volume = 0.5;
  private voices = 0;

  // --- Ambient colony bed (lazily built; a couple of oscillators + one LFO). ---
  /** Bed sub-mix: drone + fifth feed this gain, which feeds the master. */
  private bedGain: GainNode | null = null;
  private bedDrone: OscillatorNode | null = null;
  private bedFifth: OscillatorNode | null = null;
  private bedLfo: OscillatorNode | null = null;
  /** Scales the LFO output into a tremolo on bedGain.gain. */
  private bedLfoDepth: GainNode | null = null;
  /** Last requested intensity, re-applied if the bed is (re)built after a gesture. */
  private ambientLevel = 0;

  /** Lazily create the context — MUST be called from a user gesture. Idempotent. */
  init(): void {
    if (this.ready) return;
    const AC: typeof AudioContext | undefined =
      typeof window === 'undefined' ? undefined
        : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!AC) return; // no WebAudio → stay silent
    try {
      this.ac = new AC();
      this.master = this.ac.createGain();
      this.master.gain.value = this.muted ? 0 : this.volume;
      this.master.connect(this.ac.destination);
      this.ready = true;
      // If the HUD already requested an ambient level before this gesture, bring the
      // bed up to it now (otherwise it lights up on the next setAmbient at HUD cadence).
      if (this.ambientLevel > 0) this.setAmbient(this.ambientLevel);
    } catch {
      this.ac = null;
      this.master = null;
    }
  }

  /** Resume a context the browser auto-suspended (call on gesture too). */
  resume(): void {
    void this.ac?.resume?.();
  }

  /** Suspend the context — stops the ambient bed and any voices with it (symmetric
   *  with resume; the bed graph is preserved and revives on the next resume). */
  suspend(): void {
    void this.ac?.suspend?.();
  }

  /** Apply persisted settings (called on init + whenever Settings change). */
  applySettings(volume: number, muted: boolean): void {
    this.volume = clampVolume(volume);
    this.muted = muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume;
  }

  setVolume(v: number): void { this.applySettings(v, this.muted); }
  setMuted(m: boolean): void { this.applySettings(this.volume, m); }
  isReady(): boolean { return this.ready; }

  /** Play a named cue. No-op if not ready / muted / over the voice cap / unknown id. */
  play(id: CueId): void {
    if (!this.ready || this.muted || this.ac === null || this.master === null) return;
    if (this.voices >= MAX_VOICES) return;
    const cue = SOUND_BANK[id];
    if (cue === undefined) return;
    const ac = this.ac;
    const now = ac.currentTime;
    for (const t of cue.tones ?? []) this.renderTone(ac, this.master, t, now);
    for (const n of cue.noise ?? []) this.renderNoise(ac, this.master, n, now);
    this.voices++;
    const ms = cueEnd(cue) * 1000 + 60;
    window.setTimeout(() => { this.voices = Math.max(0, this.voices - 1); }, ms);
  }

  /**
   * Drive the ambient colony bed from a 0..1 intensity (the caller derives this from
   * live population/activity at the HUD cadence). Lazily builds the bed on first call
   * once the context exists (so it stays gesture-gated like everything else), then
   * just retargets gain/pitch/tremolo via AudioParam ramps — no per-call allocation.
   *
   * Fully governed by the master gain: the bed routes through `this.master`, so when
   * the engine is muted or volume is 0 the master gain is 0 and the bed is silent
   * (the oscillators keep running cheaply, but inaudible). It is also stopped/suspended
   * with the context. A no-op until the context is ready.
   */
  setAmbient(level: number): void {
    const p = ambientParams(level);
    this.ambientLevel = clampVolume(level);
    if (!this.ready || this.ac === null || this.master === null) return;
    this.ensureBed();
    if (this.bedGain === null || this.bedDrone === null || this.bedFifth === null ||
        this.bedLfo === null || this.bedLfoDepth === null) return;
    const ac = this.ac;
    const now = ac.currentTime;
    const T = 0.5; // smoothing window so changes glide, never click
    // Pitch: glide the two voices to the new fundamental + its fifth.
    this.bedDrone.frequency.linearRampToValueAtTime(Math.max(1, p.baseHz), now + T);
    this.bedFifth.frequency.linearRampToValueAtTime(Math.max(1, p.fifthHz), now + T);
    // Tremolo: bedGain.gain oscillates around a base; the LFO adds ±depth. Choose
    // base = gain*(1 - lfoDepth) and depth = gain*lfoDepth so the swell peaks at
    // `gain` and the trough stays >= 0 (never negative, never clicks).
    const base = p.gain * (1 - p.lfoDepth);
    const depth = p.gain * p.lfoDepth;
    this.bedGain.gain.linearRampToValueAtTime(base, now + T);
    this.bedLfoDepth.gain.linearRampToValueAtTime(depth, now + T);
    this.bedLfo.frequency.linearRampToValueAtTime(Math.max(0.01, p.lfoHz), now + T);
  }

  /** Build the ambient graph once (idempotent). Oscillators run for the context's
   *  lifetime; intensity only retargets their params (see setAmbient). */
  private ensureBed(): void {
    if (this.bedGain !== null || this.ac === null || this.master === null) return;
    const ac = this.ac;
    const p = ambientParams(this.ambientLevel);
    // Sub-mix gain (the tremolo target), starting silent so the bed fades in.
    const bedGain = ac.createGain();
    bedGain.gain.value = 0.0001;
    bedGain.connect(this.master);
    // Two consonant voices a fifth apart.
    const drone = ac.createOscillator();
    drone.type = 'sine';
    drone.frequency.value = Math.max(1, p.baseHz);
    const fifth = ac.createOscillator();
    fifth.type = 'triangle';
    fifth.frequency.value = Math.max(1, p.fifthHz);
    // A quiet fixed mix for the fifth so the fundamental dominates.
    const fifthMix = ac.createGain();
    fifthMix.gain.value = 0.5;
    drone.connect(bedGain);
    fifth.connect(fifthMix).connect(bedGain);
    // Slow LFO → depth gain → modulates bedGain.gain (tremolo / "breathing").
    const lfo = ac.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = Math.max(0.01, p.lfoHz);
    const lfoDepth = ac.createGain();
    lfoDepth.gain.value = 0.0001;
    lfo.connect(lfoDepth).connect(bedGain.gain);
    try {
      drone.start();
      fifth.start();
      lfo.start();
    } catch {
      // If start throws (already-started in a reused node), leave the bed unbuilt
      // rather than half-wired; setAmbient will retry on the next call.
      return;
    }
    this.bedGain = bedGain;
    this.bedDrone = drone;
    this.bedFifth = fifth;
    this.bedLfo = lfo;
    this.bedLfoDepth = lfoDepth;
  }

  private renderTone(ac: AudioContext, out: GainNode, t: ToneLayer, now: number): void {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    const start = now + (t.delay ?? 0);
    const end = start + t.attack + t.decay;
    osc.type = t.wave;
    osc.frequency.setValueAtTime(t.freq, start);
    if (t.freqEnd !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, t.freqEnd), end);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(t.gain, start + t.attack);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(g).connect(out);
    osc.start(start);
    osc.stop(end + 0.02);
  }

  private renderNoise(ac: AudioContext, out: GainNode, n: NoiseLayer, now: number): void {
    const start = now + (n.delay ?? 0);
    const end = start + n.attack + n.decay;
    const len = Math.max(1, Math.floor(ac.sampleRate * (n.attack + n.decay)));
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1; // cosmetic noise (never sim RNG)
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.linearRampToValueAtTime(n.gain, start + n.attack);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    let node: AudioNode = src;
    if (n.filterHz !== undefined) {
      const filt = ac.createBiquadFilter();
      filt.type = 'bandpass';
      filt.frequency.value = n.filterHz;
      src.connect(filt);
      node = filt;
    }
    node.connect(g).connect(out);
    src.start(start);
    src.stop(end + 0.02);
  }
}

/** The process-wide audio engine (render-only singleton). */
export const audio = new AudioEngine();
